import { Card } from "antd";
import { useAntTheme } from "hooks/useAntTheme";
import React, { useEffect, useRef, useState } from "react";
import { ProjectGeneratingState } from "./ProjectGeneratingState";
import { ProjectPreview } from "./ProjectPreview";
import { IProjectDetails } from "./types";
import { computeIsMediaGenerating } from "../helpers/helper";
import { radius } from "@libertify/ui";

interface ProjectPreviewCardProps {
  refetchProjectDetail: () => void;
  projectDetails: IProjectDetails;
  backgroundImg?: string;
}

export const ProjectPreviewCard: React.FC<ProjectPreviewCardProps> = ({
  refetchProjectDetail,
  projectDetails,
  backgroundImg,
}) => {
  const { theme } = useAntTheme();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [videoMetadata, setVideoMetadata] = useState<{
    duration: number;
    width: number;
    height: number;
    aspectRatio: number;
  } | null>(null);

  // Load video metadata to get actual dimensions
  useEffect(() => {
    if (projectDetails.videoUrl) {
      const handleLoadedMetadata = () => {
        if (videoRef.current?.videoWidth && videoRef.current?.videoHeight) {
          setVideoMetadata({
            width: videoRef.current.videoWidth,
            height: videoRef.current.videoHeight,
            duration: videoRef.current.duration,
            aspectRatio:
              videoRef.current.videoWidth / videoRef.current.videoHeight,
          });
        }
      };

      const videoElement = videoRef.current;
      if (!videoElement) return;

      videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);

      // If metadata is already loaded
      if (videoElement.readyState >= 1) {
        handleLoadedMetadata();
      }

      return () => {
        videoElement.removeEventListener(
          "loadedmetadata",
          handleLoadedMetadata
        );
      };
    }
  }, [projectDetails.videoUrl]);

  const isGenerating = computeIsMediaGenerating(
    projectDetails.project.status,
    projectDetails.mediaStatus
  );

  return (
    <Card
      style={{
        borderRadius: radius.box,
        border: theme.cardBorder,
        backgroundColor: theme.colorCardRedesign,
        boxShadow: theme.cardBoxShadow,
        width: "100%",
      }}
    >
      {isGenerating ? (
        <ProjectGeneratingState userEmail={projectDetails.owner?.email} />
      ) : (
        <ProjectPreview
          backgroundImg={backgroundImg}
          videoMetadata={videoMetadata}
          videoRef={videoRef}
          refetchProjectDetail={refetchProjectDetail}
          projectDetails={projectDetails}
        />
      )}
    </Card>
  );
};
